import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  deepPurpleMui,
  greyMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

export const PassengersGridMapStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "passengers-grid-map-wrapper",
})`
  height: 100%;
  width: 100%;
  /* The walk card's chrome and contents travel with MapLibreWalkingRoute now, not with this screen. */
  div.map_scrollable_content {
    min-width: max(100%, 250px);
    height: 100%;
  }
`;

export const MapButtonsControlsStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "map-buttons-controls-wrapper",
})`
  top: 130px;
  inset-inline-end: 12px;
  position: absolute;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
  display: flex;
  flex-direction: column;
  width: fit-content;

  .mapControlBtn {
    cursor: pointer;
    border-radius: 3px;
    border: 1px solid ${blueGreyMui._200};
    background: ${rydeColors.white};
    display: inline-block;
    padding: 5px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${rydeColors.black};

    &.selected {
      color: ${deepPurpleMui.A700};
    }

    &.disabled {
      color: ${blueGreyMui.A200};
      pointer-events: none;
      cursor: default;

      &.warning {
        color: ${blueGreyMui.A700};
      }
    }

    &.warning {
      background: ${rydeColors.peachCoral};
    }
  }
`;

export const AddNewStationPopperContentWrapper = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "add-new-station-content-wrapper",
  },
)`
  inset-inline-start: 0;
  display: inline-flex;
  padding: 10px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 5px;
  border: 1px solid ${greyMui._300};
  background-color: ${rydeColors.smoke};
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  min-width: 100%;

  .add-station-control-option-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 35px;
    white-space: nowrap;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;

    &:hover {
      transform: scale(1.02);
      border-bottom: 0.5px solid ${greyMui._300};
    }

    &.disabled {
      cursor: default;
      color: ${greyMui._500};
      pointer-events: none;
    }
  }
`;

export const PassengersFilterStationsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "passengers-filter-stations-panel-wrapper",
  },
)`
  cursor: default;
  height: 75px;
  width: 100%;
  padding: 7px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${rydeColors.white};

  top: 0;
  position: relative;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};

  .dropdownInput {
    width: 400px;
  }

  .actionsButtons {
    display: flex;
    align-items: center;
    gap: 15px;

    .saveButton {
      cursor: pointer;
      padding: 8px 35px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      color: ${rydeColors.white};
      background: ${deepPurpleMui.A700};

      &.disabled {
        background-color: ${blueGreyMui.A200};
        color: ${rydeColors.googleAttributes};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        pointer-events: none;
      }
    }

    .close-icon-svg {
      cursor: pointer;
    }
  }
`;

export const PassengersLinkStationsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "passengers-link-stations-panel-wrapper",
  },
)`
  cursor: default;
  height: 75px;
  width: 100%;
  padding: 7px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${rydeColors.white};

  top: 0;
  position: relative;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};

  .dropdownsContainer {
    display: flex;
    align-items: center;
    gap: 4px;

    .dropdownInput {
      width: 230px;

      &.multiSelect {
        width: 280px;
      }

      .MuiFormControl-root {
        height: 56px;
      }

      .MuiOutlinedInput-root {
        background: ${rydeColors.white};
        display: flex;
      }

      .MuiAutocomplete-inputRoot {
        flex-wrap: nowrap;

        &.Mui-focused {
          flex-wrap: wrap;
        }
      }
    }
  }

  .actionsButtons {
    display: flex;
    align-items: center;
    gap: 15px;

    .saveButton {
      cursor: pointer;
      padding: 8px 35px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      color: ${rydeColors.white};
      background: ${deepPurpleMui.A700};

      &.disabled {
        background-color: ${blueGreyMui.A200};
        color: ${rydeColors.googleAttributes};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        pointer-events: none;
      }
    }

    .close-icon-svg {
      cursor: pointer;
    }
  }
`;

export const PassengersAddStationPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "passengers-add-station-panel-wrapper",
  },
)`
  cursor: default;
  height: 75px;
  width: 100%;
  padding: 7px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${rydeColors.white};
  top: 0;
  position: relative;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};

  .addressAutoCompleteInput {
    margin-bottom: 5px;
    width: 100%;
  }

  .dropdownsContainer {
    display: flex;
    align-items: center;
    gap: 4px;

    .dropdownInput {
      width: 450px;
    }
  }

  .actionsButtons {
    display: flex;
    align-items: center;
    gap: 15px;

    .saveButton {
      cursor: pointer;
      padding: 8px 35px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      color: ${rydeColors.white};
      background: ${deepPurpleMui.A700};

      &.disabled {
        background-color: ${blueGreyMui.A200};
        color: ${rydeColors.googleAttributes};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        pointer-events: none;
      }
    }

    .close-icon-svg {
      cursor: pointer;
    }
  }
`;

export const PassengersAddBulkStationsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "passengers-add-bulk-stations-panel-wrapper",
  },
)`
  cursor: default;
  height: 110px;
  width: 100%;
  padding: 7px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${rydeColors.white};

  top: 0;
  position: relative;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};

  .dropdownsContainer {
    display: flex;
    align-items: center;
    gap: 4px;

    .dropdownInput,
    .input {
      width: 250px;
    }
  }

  .actionsButtons {
    display: flex;
    align-items: center;
    gap: 15px;

    .saveButton {
      cursor: pointer;
      padding: 8px 35px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      color: ${rydeColors.white};
      background: ${deepPurpleMui.A700};

      &.disabled {
        background-color: ${blueGreyMui.A200};
        color: ${rydeColors.googleAttributes};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        pointer-events: none;
      }
    }

    .close-icon-svg {
      cursor: pointer;
    }
  }
`;
